// 調達必要量 (need) の frontend 合算ロジック。
//
// backend の GetProcurementNeed は per-area 48 コマ need を返す。ポジションチャートは
// チェック中エリアの need をコマ別に合算した基準線を描く (設計書 §4 area 集約 / grill Q5=A:
// フィルタは動的クライアント状態なので frontend で合算する)。
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type { ProcurementNeedAreaSeries } from '@ebalance/proto/bffuser/v1/supply_demand_gap_model_pb'

import { KOMA_COUNT } from '~/constants/koma'
import { decimalToNumberOr } from '~/models/decimal'

/**
 * per-area need series からチェック中エリアの need をコマ別に合算した 48 要素配列を返す
 * (index i = コマ i+1)。チェックされていないエリアは無視する。need_mw は Decimal (string)
 * なので `decimalToNumberOr` で数値化する (= 未設定/空文字/非数値は 0 とみなし、 生
 * `parseFloat` の NaN が合算全体を汚染するのを防ぐ)。
 */
export const combineNeedByKoma = (
  series: ProcurementNeedAreaSeries[],
  checkedAreas: Set<AreaCode>,
): number[] => {
  const out = new Array<number>(KOMA_COUNT).fill(0)
  for (const s of series) {
    if (!checkedAreas.has(s.areaCode)) continue
    for (const p of s.points) {
      const idx = p.komaNumber - 1
      if (idx < 0 || idx >= KOMA_COUNT) continue
      out[idx] += decimalToNumberOr(p.needMw, 0)
    }
  }
  return out
}

/**
 * チャート対象エリアの need をコマ別に合算する。 対象エリアの series が応答に無い時は
 * undefined (need 線を描かない)。 発注主体区分を指定した need は、 その区分が計画を持たない
 * エリアの series を返さず (AC-BSP-102、 #10574)、 0 で埋めると「過不足なし」 に見えるため。
 */
export const needOfChartAreas = (
  series: ProcurementNeedAreaSeries[],
  chartAreas: Set<AreaCode>,
): number[] | undefined => {
  const returned = new Set(series.map((s) => s.areaCode))
  for (const area of chartAreas) {
    if (!returned.has(area)) return undefined
  }
  return combineNeedByKoma(series, chartAreas)
}

/**
 * per-area need series を「エリア → 48 コマ need(MW) 配列」の Map に展開する
 * (index i = コマ i+1)。合算する `combineNeedByKoma` と違い、エリアごとの need を
 * そのまま保持する。一括作成 (AC-005a 案B) はエリア別・コマ別に数量を算出するため合算しない。
 * need の符号 (正 = 不足 / 負 = 余剰) はそのまま保つ (需要 − 確保済供給力、proto コメント準拠)。
 */
export const perAreaNeedByKoma = (
  series: ProcurementNeedAreaSeries[],
): Map<AreaCode, number[]> => {
  const map = new Map<AreaCode, number[]>()
  for (const s of series) {
    const arr = map.get(s.areaCode) ?? new Array<number>(KOMA_COUNT).fill(0)
    for (const p of s.points) {
      const idx = p.komaNumber - 1
      if (idx < 0 || idx >= KOMA_COUNT) continue
      arr[idx] += decimalToNumberOr(p.needMw, 0)
    }
    map.set(s.areaCode, arr)
  }
  return map
}
